import type React from "react";
import { useEffect, useRef } from "react";

import { useRemirrorContext } from "@remirror/react";
import { styled } from "styled-components";

import type { SemanticICONS } from "simplydo/react/ui";
import { Icon, Popup } from "simplydo/react/ui";

import { Help } from "./Help";
import { useFocusState } from "./useFocusState";

const StickyMenuBar = styled.div<{ $stickyOffset: number }>`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 4px;

  position: sticky;
  min-height: 40px;
  top: ${(props) => props.$stickyOffset}px;
  z-index: 1;
  background-color: white;
  padding: 5px 5px;
  box-shadow: 0 0 0 1px rgb(0, 0, 0, 0.1);
  margin-bottom: 10px;
  border-radius: 5px;

  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
      overflow: auto;
      scrollbar-width: thin;
      padding-bottom: 4px;
    `}
`;

const MenuBar = styled.div<{ $stickyOffset: number }>`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 4px;

  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
      overflow: auto;
      scrollbar-width: thin;
      padding-bottom: 4px;
      margin-bottom: 0;
    `}
`;

const Align = styled.div<{ $right?: boolean }>`
  display: flex;
  gap: 3px;

  flex-direction: row;
  flex-wrap: ${({ theme }) => (theme.sizes.isMobile ? "nowrap" : "wrap")};

  justify-content: ${(props) => (props.$right ? "flex-end" : "flex-start")};
`;

const Separator = styled.div`
  width: 1px;
  background-color: #ddd;
  height: 18px;
  align-self: center;
  justify-self: center;
  margin-left: 4px;
  margin-right: 4px;
`;

const IconWrapper = styled.div<{ $isActive: boolean; $disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  padding: 5px;

  background-color: none;
  box-shadow: ${({ $isActive }) =>
    $isActive ? "inset 4px 6px 11px 4px rgba(0,0,0,0.25)" : "none"};

  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &:active {
    background-color: none;
    box-shadow: inset 2px 3px 8px 0px rgba(0, 0, 0, 0.15);
  }

  i {
    top: -2px;
    left: 2px;
  }
`;

const StyledIcon = styled(Icon)`
  && {
    flex-direction: row;
    font-size: 16px;
    position: relative;
  }
`;

const IconChar = styled.div`
  display: inline;
  left: -2px;
  vertical-align: top;
  top: -1px;
  position: relative;
`;

export type MenuCommands = {
  // Keep this intentionally small and explicit.
  // It’s passed to `extraMenuItems` so they don’t need to know about chain/run.
  toggleBold?: () => void;
  toggleItalic?: () => void;
  toggleUnderline?: () => void;
  toggleStrike?: () => void;
  toggleHeading?: (opts: { level: number }) => void;
  toggleBulletList?: () => void;
  toggleOrderedList?: () => void;
  setAlignment?: (opts: { direction: "center" | "right" }) => void;
  removeAlignment?: () => void;

  undo?: () => void;
  redo?: () => void;

  insertText?: (text: string) => void;
  updateLink?: (
    attrs: { href: string },
    range?: { from: number; to: number },
  ) => void;
  removeLink?: () => void;

  // Used by preset menus (e.g. `getFullMenu`)
  createCustomLink?: (data: { text?: string; href: string }) => void;
  insertLink?: (data: { href: string }) => void;

  insertImage?: (data: { src: string }) => void;
  insertVideo?: (data: { src: string }) => void;
  addIframe?: (data: { src: string; title?: string; allow?: string }) => void;
  insertFile?: (data: { name: string; src: string }) => void;
};

export type ExtraMenuItemAttrs = {
  name: string;
  commands: MenuCommands;
  selectedText: string;
  isActive: boolean;
};

export type ExtraMenuItem = {
  name: string;
  icon?: SemanticICONS | string;
  top?: number | undefined;
  left?: number | undefined;
  style?: React.CSSProperties;
  content?: React.ReactNode;
  forceEnable?: boolean;
  disabled?: boolean;
  onClick?: (attrs: ExtraMenuItemAttrs) => void;
  active?: () => boolean;
  MenuItem?: React.ComponentType<{
    RenderItem: React.ComponentType<ExtraMenuItemRenderProps>;
    itemProps: ExtraMenuItemRenderProps;
  }>;
};

type MenuIconProps = {
  isActive: boolean;
  name: SemanticICONS | string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
  top?: number | undefined;
  left?: number | undefined;
  style?: React.CSSProperties | undefined;
  disabled?: boolean | undefined;
};

const MenuIcon = ({
  isActive,
  onClick,
  children,
  top,
  left,
  name,
  style,
  disabled,
  ...props
}: MenuIconProps) => (
  <IconWrapper
    onClick={onClick}
    $isActive={!!isActive}
    $disabled={!!disabled}
    style={style}
    {...props}
  >
    <StyledIcon $top={top} $left={left} name={name as SemanticICONS} />
    {children}
  </IconWrapper>
);

type ExtraMenuItemRenderProps = { item: ExtraMenuItem };

type EditMenuProps = {
  extraMenuItems?: Array<ExtraMenuItem> | undefined;
  sticky?: boolean;
  stickyOffset?: number;
  historyEnabled?: boolean;
  autoHideMenu?: boolean;
  keepMenuOpen?: boolean;
};

export const EditMenu = ({
  extraMenuItems = [],
  sticky,
  stickyOffset,
  historyEnabled,
  autoHideMenu,
  keepMenuOpen,
}: EditMenuProps) => {
  const rem = useRemirrorContext({ autoUpdate: true });
  const isFocused = useFocusState();

  const { getState, active, chain, commands } = rem;
  const { selection, doc, history$ } = getState();

  const selectionRef = useRef(selection);
  const docRef = useRef(doc);

  useEffect(() => {
    selectionRef.current = selection;
    docRef.current = doc;
  }, [selection, doc]);

  const getSelectedText = () => {
    const sel = selectionRef.current;
    const currentDoc = docRef.current;
    if (!sel || !currentDoc) return "";
    try {
      return currentDoc.cut(sel.from, sel.to).textContent;
    } catch {
      return "";
    }
  };

  const focus = () => chain.focus();
  const menuCommands: MenuCommands = {
    toggleBold: () => focus().toggleBold().run(),
    toggleItalic: () => focus().toggleItalic().run(),
    toggleUnderline: () => focus().toggleUnderline().run(),
    toggleStrike: () => focus().toggleStrike().run(),
    toggleHeading: (opts) => focus().toggleHeading(opts).run(),
    toggleBulletList: () => focus().toggleBulletList().run(),
    toggleOrderedList: () => focus().toggleOrderedList().run(),
    setAlignment: (opts) => focus().setAlignment(opts).run(),
    removeAlignment: () => focus().removeAlignment().run(),

    undo: () => {
      commands.undo();
      focus().run();
    },
    redo: () => {
      commands.redo();
      focus().run();
    },

    insertText: (text) => focus().insertText(text).run(),
    updateLink: (attrs, range) => {
      if (range) {
        focus().updateLink(attrs, range).run();
        return;
      }
      focus().updateLink(attrs).run();
    },
    removeLink: () => focus().removeLink().run(),

    createCustomLink: ({ text, href }) => {
      const url = text || href;
      const sel = selectionRef.current;
      if (!sel) return;

      if (sel.from === sel.to) {
        focus()
          .insertText(`${url} `)
          .updateLink({ href }, { from: sel.from, to: sel.from + url.length })
          .run();
      } else {
        focus().updateLink({ href }).run();
      }
    },
    insertLink: ({ href }) => {
      const sel = selectionRef.current;
      if (!sel) return;
      focus()
        .insertText(`${href} `)
        .updateLink({ href }, { from: sel.from, to: sel.from + href.length })
        .run();
    },

    insertImage: (data) => focus().insertImage(data).run(),
    insertVideo: (data) => focus().insertVideo(data).run(),
    addIframe: (data) => focus().addIframe(data).run(),
    insertFile: (data) => focus().insertFile(data).run(),
  };

  const renderExtraItem = (item: ExtraMenuItem, key: number) => {
    const activeMap = active as unknown as Record<string, unknown>;
    const hasBuiltIn = !!activeMap?.[item.name] || item.forceEnable;
    if (!hasBuiltIn) return null;

    let isActive = false;
    try {
      const activeFn = activeMap?.[item.name];
      isActive = !!(
        item.active ||
        (typeof activeFn === "function" ? (activeFn as () => boolean) : null) ||
        (() => false)
      )();
    } catch {
      isActive = false;
    }

    const disabled = !!item.disabled;

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      item.onClick?.({
        name: item.name,
        commands: menuCommands,
        selectedText: getSelectedText(),
        isActive,
      });
    };

    const button = (
      <MenuIcon
        name={item.icon || item.name}
        top={item.top}
        left={item.left}
        style={item.style}
        disabled={disabled}
        isActive={isActive}
        onClick={handleClick}
      >
        {item.content}
      </MenuIcon>
    );

    if (item.MenuItem) {
      const Wrapper = item.MenuItem;
      const RenderItem = ({ item: _item }: ExtraMenuItemRenderProps) => button;
      return <Wrapper key={key} RenderItem={RenderItem} itemProps={{ item }} />;
    }

    return <span key={key}>{button}</span>;
  };

  if (autoHideMenu && !isFocused && !keepMenuOpen) {
    return null;
  }

  const MenuComponent = sticky ? StickyMenuBar : MenuBar;

  return (
    <MenuComponent
      className="ProseMirror-menubar"
      $stickyOffset={stickyOffset ?? 0}
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Align>
        <MenuIcon
          name="bold"
          isActive={!!active.bold?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleBold?.();
          }}
        />
        <MenuIcon
          name="italic"
          isActive={!!active.italic?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleItalic?.();
          }}
        />
        <MenuIcon
          name="underline"
          top={2}
          isActive={!!active.underline?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleUnderline?.();
          }}
        />
        <MenuIcon
          name="strikethrough"
          isActive={!!active.strike?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleStrike?.();
          }}
        />
        <Separator />

        <MenuIcon
          name="heading"
          isActive={!!active.heading?.({ level: 1 })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleHeading?.({ level: 1 });
          }}
        >
          <IconChar>1</IconChar>
        </MenuIcon>
        <MenuIcon
          name="heading"
          isActive={!!active.heading?.({ level: 2 })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleHeading?.({ level: 2 });
          }}
        >
          <IconChar>2</IconChar>
        </MenuIcon>
        <Separator />

        <MenuIcon
          name="list ul"
          isActive={!!active.bulletList?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleBulletList?.();
          }}
        />
        <MenuIcon
          name="list ol"
          isActive={!!active.orderedList?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.toggleOrderedList?.();
          }}
        />
        <Separator />

        <MenuIcon
          name="align left"
          isActive={!active.alignment?.()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.removeAlignment?.();
          }}
        />
        <MenuIcon
          name="align center"
          isActive={!!active.alignment?.({ direction: "center" })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.setAlignment?.({ direction: "center" });
          }}
        />
        <MenuIcon
          name="align right"
          isActive={!!active.alignment?.({ direction: "right" })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuCommands.setAlignment?.({ direction: "right" });
          }}
        />
        <Separator />

        {extraMenuItems.map((item, idx) => renderExtraItem(item, idx))}
      </Align>
      <Align $right>
        {historyEnabled ?
          <>
            <MenuIcon
              name="undo alternate"
              disabled={history$.done.eventCount === 0}
              isActive={false}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                menuCommands.undo?.();
              }}
            />
            <MenuIcon
              name="redo alternate"
              disabled={history$.undone.eventCount === 0}
              isActive={false}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                menuCommands.redo?.();
              }}
            />
            <Separator />
          </>
        : null}
        <Popup
          position="top right"
          wide="very"
          inverted
          on="hover"
          trigger={
            <div>
              <MenuIcon isActive={false} name="help" />
            </div>
          }
          content={<Help />}
          header="Tips"
        />
      </Align>
    </MenuComponent>
  );
};
